// src/pages/bluebi/PainelView.tsx
// Exibição de um painel em tela cheia, na aba aberta a partir do card (issue #42).
//
// ⚠️ A URL desta rota carrega o SLUG do rótulo (`/bluebi/vendas`), NUNCA o link do
// Power B.I. — que é anônimo, público e permanente (issue #42 §1). O link é resolvido
// aqui, a partir da listagem, e vive apenas dentro do `src` do iframe: fora da barra de
// endereços, fora do histórico, fora do `referer` e fora de qualquer compartilhamento
// de tela que mostre a URL.
//
// O gate de rota do `ProtectedRoute` casa o path por igualdade exata, então não alcança
// esta subrota — por isso os dois eixos (permissão + módulo contratado) são conferidos
// localmente, com a mesma regra que ele aplicaria.
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlement } from '../../contexts/EntitlementContext';
import { apiGet } from '../../services/api';
import { logger } from '@/utils/logger';
import { PainelGestao, painelPodeAbrir, slugDoRotulo } from '../../types/bluebi/Painel';
import './BlueBi.css';

const PainelView = () => {
  const { nome } = useParams<{ nome: string }>();
  const { theme } = useTheme();
  const { hasPermission } = useAuth();
  const { isAreaEnabled } = useEntitlement();
  const navigate = useNavigate();
  const [painel, setPainel] = useState<PainelGestao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const podeEditar = hasPermission('bluebi.editar');
  // Gate ANTES do request: o Navigate abaixo acontece no render, mas o efeito de carga já
  // teria sido agendado no mount — sem esta guarda, quem cai aqui sem acesso dispara um
  // GET que só pode responder 400.
  const podeCarregar =
    hasPermission('modulo.bluebi') &&
    isAreaEnabled('bluebi') &&
    (podeEditar || hasPermission('bluebi.visualizar'));

  const carregarPainel = useCallback(async () => {
    if (!podeCarregar) {
      setCarregando(false);
      return;
    }
    setCarregando(true);
    setErro(null);
    try {
      const rota = podeEditar ? '/v1/bluebi/gestao/paineis' : '/v1/bluebi/paineis';
      const response = await apiGet(rota);
      const lista: PainelGestao[] = Array.isArray(response.data) ? response.data : [];
      // Casa pelo slug do rótulo. Quem edita lê a rota de gestão, que TRAZ os desativados
      // — então o painel pode chegar aqui inativo, e é o render que recusa abri-lo.
      setPainel(lista.find((p) => slugDoRotulo(p.rotulo) === nome) ?? null);
    } catch (err) {
      logger.error('Falha ao carregar o painel:', err instanceof Error ? err.message : String(err));
      // Mensagem própria — o corpo da API não é ecoado (§E / issue #42 §1).
      setErro('Não foi possível carregar o painel. Tente novamente.');
    } finally {
      setCarregando(false);
    }
  }, [podeEditar, podeCarregar, nome]);

  useEffect(() => {
    carregarPainel();
  }, [carregarPainel]);

  useEffect(() => {
    document.title = painel ? `Bluelt | ${painel.rotulo}` : 'Bluelt | Blue B.I.';
  }, [painel]);

  if (!hasPermission('modulo.bluebi') || !isAreaEnabled('bluebi')) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div className={`bluebi-page ${theme}`}>
      {carregando && <p>Carregando...</p>}
      {erro && <p className="error-message">{erro}</p>}
      {!carregando && !erro && !painel && (
        <div className="bluebi-vazio">
          <p>Painel não encontrado.</p>
          <button type="button" className="option-button" onClick={() => navigate('/bluebi')}>
            Voltar para o Blue B.I.
          </button>
        </div>
      )}
      {/* Painel fora de circulação NÃO abre — nem aqui, nem para quem tem `bluebi.editar`,
          nem para ROLE_MASTER. Esta tela é alcançável por URL direta, então a checagem tem
          de existir aqui e não só no clique do card: quem tem o endereço no histórico
          chegaria direto ao relatório. `bluebi.editar` dá o direito de administrar o
          painel — inclusive de reativá-lo —, não o de consumir o que foi retirado do ar. */}
      {!carregando && !erro && painel && !painelPodeAbrir(painel) && (
        <div className="bluebi-vazio">
          <p>Este painel está desativado e não pode ser aberto.</p>
          <button type="button" className="option-button" onClick={() => navigate('/bluebi')}>
            Voltar para o Blue B.I.
          </button>
        </div>
      )}
      {!carregando && !erro && painel && painelPodeAbrir(painel) && (
        // Sem título na tela: o nome do painel já identifica a aba (`document.title`),
        // e o espaço que ele ocupava vale mais para o relatório.
        <iframe
          src={painel.link}
          title={painel.rotulo}
          className="bluebi-painel-cheio"
          allowFullScreen
        />
      )}
    </div>
  );
};

export default PainelView;
